import type { Addon } from '../../types/addons';

import { cn } from '@/shared/lib/utils';
import { getImageUrl } from '@/shared/utils/image';
import { ExpandableText } from '@/shared/components/ui/expandable-text';
import { getAddonLimitLabel } from '../../utils/addon';

interface AddonsItemProps {
  addon: Addon;
  // Foto menor (48px) — versão enxuta do atendimento/PDV.
  compact?: boolean;
}

export function AddonsItem({ addon, compact = false }: AddonsItemProps) {
  const priceLabel =
    addon.price !== '0.00'
      ? Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(addon.price))
      : '';

  // Legenda do teto individual do complemento (issue 311): fica sob o nome,
  // vazia quando o complemento não tem limite próprio.
  const individualLimitLabel = getAddonLimitLabel(addon.select_max);

  return (
    // `items-start` mantém a imagem e o controle (à direita, renderizado pelo
    // pai) alinhados ao topo mesmo quando a descrição é expandida.
    <div className={cn('flex items-start w-full', compact ? 'gap-2.5' : 'gap-3')}>
      <div
        className={cn(
          'overflow-hidden flex-shrink-0 bg-gray-100',
          compact ? 'size-12 rounded-xl' : 'w-16 h-16 rounded-3xl'
        )}
      >
        <img
          src={getImageUrl(addon?.image)}
          alt={addon.name}
          className="w-full h-full object-cover object-center"
          onError={e => {
            e.currentTarget.src = '/fallback.svg';
          }}
        />
      </div>
      {/*
        Coluna de conteúdo em `flex-col`. No mobile, o preço sobe para logo
        abaixo do nome (order-2), ficando em linha própria e visível sem rolar a
        descrição; a partir de `sm` ele volta para o fim do card (order-3).
      */}
      <div className="flex flex-col flex-1 min-w-0 gap-px">
        <h1 className="order-1 text-night text-sm font-bold line-clamp-2 break-words">
          {addon.name}
        </h1>
        {individualLimitLabel && (
          <p className="order-1 text-gray-500 text-[11px] font-normal">{individualLimitLabel}</p>
        )}
        {priceLabel && (
          <p className="order-2 sm:order-3 w-full text-night text-xs font-semibold">{priceLabel}</p>
        )}
        {addon.description && (
          <ExpandableText
            collapsedLines={2}
            expandLabel="Ver mais"
            collapseLabel="Ver menos"
            className="order-3 sm:order-2 text-gray-500 text-xs font-normal"
            triggerClassName="text-primary"
            // A opção inteira é clicável para selecioná-la; impedimos que o
            // clique no gatilho "Ver mais"/"Ver menos" borbulhe e altere a
            // seleção do complemento.
            onClick={e => e.stopPropagation()}
          >
            {addon.description}
          </ExpandableText>
        )}
      </div>
    </div>
  );
}
